import React, { useState, useRef, useEffect } from "react";
import Tooltip from "@material-ui/core/Tooltip";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { toast } from "react-toastify";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import AppBar from "@mui/material/AppBar";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import Container from "../../GeneralComponents/Container";
import { Topbar, Sidebar, Table } from "../components";
import { getStudents, deleteStudent, restoreStudent, getOrganizations, getBatches } from "../../../redux/actions/apiActions";
import setGlobalLoader from "../../../redux/actions/loaderActions";
import { PROFILES, STUDENT_STATUS } from "../../../utils/constants";
import exportToExcel from "../../../utils/exportToExcel";
import { Stack, Autocomplete, Chip, MenuItem, Paper } from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";

const TableComponent = React.forwardRef(({ columns, data, title, isRemoteData }, ref) => {
  const theme = useTheme();

  return (
    <Box p={4}>
      <Box
        width={1}
        height={1}
        // minHeight={800}
        borderRadius={2}
        border={`2px solid ${theme.palette.divider}`}
        sx={{
          borderStyle: "dashed",
        }}
      >
        <Table
          ref={ref}
          columns={columns}
          data={data}
          export={false}
          // search
          selection={false}
          toolbar
          title={title}
          isRemoteData={isRemoteData}
        />
      </Box>
    </Box>
  );
});

const Students = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [students, setStudents] = useState([]);
  const theme = useTheme();
  const role = useSelector((state) => state.authReducer.role);
  const profile = useSelector((state) => state.authReducer.profile);
  const tableRef = useRef(null);
  const isMd = useMediaQuery(theme.breakpoints.up("md"), {
    defaultMatches: true,
  });

  const [openSidebar, setOpenSidebar] = useState(false);

  // Date filter state - Admin: 1 month default, Organization: no default dates
  const today = new Date();
  const minDateObj = new Date("2025-01-01");

  // Calculate default dates based on profile
  const getDefaultStartDate = () => {
    if (profile === PROFILES.ADMIN) {
      return new Date(today.getFullYear(), today.getMonth(), 1);
    }
    return null; // No default for Organization
  };

  const getDefaultEndDate = () => {
    if (profile === PROFILES.ADMIN) {
      return today;
    }
    return null; // No default for Organization
  };

  const [dateRange, setDateRange] = useState([
    {
      startDate: getDefaultStartDate(),
      endDate: getDefaultEndDate(),
      key: "selection",
    },
  ]);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Getter functions for startDate and endDate
  const startDate = dateRange[0].startDate;
  const endDate = dateRange[0].endDate;

  // Handle date range change
  const handleDateRangeChange = (item) => {
    setDateRange([item.selection]);
  };
  const [searchTerm, setSearchTerm] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [paymentFilter, setPaymentFilter] = useState("");
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.onQueryChange();
    }
  }, [showDeleted]);

  // Fetch organizations for admin dropdown
  useEffect(() => {
    const fetchOrganizations = async () => {
      if (profile === PROFILES.ADMIN) {
        try {
          const orgRes = await getOrganizations("?limit=10000");
          setOrganizations(orgRes?.data?.data?.docs || []);
        } catch (error) {
          console.log("Error fetching organizations", error);
        }
      }
    };
    fetchOrganizations();
  }, [profile]);

  // Fetch batches for batch filter
  useEffect(() => {
    const fetchBatches = async () => {
      try {
        const batchRes = await getBatches("?limit=10000");
        setBatches(batchRes?.data?.data?.docs || []);
      } catch (error) {
        console.log("Error fetching batches", error);
      }
    };
    fetchBatches();
  }, []);

  const handleSidebarOpen = () => {
    setOpenSidebar(true);
  };

  const handleSidebarClose = () => {
    setOpenSidebar(false);
  };
  const open = isMd ? false : openSidebar;

  const handleDeleteStudent = async (rowData) => {
    let text = `Are you sure you want to delete ${rowData.name}`;
    if (window.confirm(text) === true) {
      dispatch(setGlobalLoader(true));
      try {
        await deleteStudent( rowData._id);
        setStudents(students.filter((d) => d._id !== rowData._id));
        toast.success("Student deleted successfully.");
        dispatch(setGlobalLoader(false));
      } catch (error) {
        dispatch(setGlobalLoader(false));
        console.log("delete student error", error);
        toast.error(
          error.response.data.msg ??
          "Some error occurred while deleting student"
        );
      }
    } else {
      toast.success("Request Cancelled");
    }
  };

  const handleRestoreStudent = async (rowData) => {
    if (window.confirm(`Restore ${rowData.name}?`) === true) {
      dispatch(setGlobalLoader(true));
      try {
        await restoreStudent(rowData._id);
        toast.success("Student restored successfully.");
        if (tableRef.current) tableRef.current.onQueryChange();
        dispatch(setGlobalLoader(false));
      } catch (error) {
        dispatch(setGlobalLoader(false));
        toast.error(error?.response?.data?.msg ?? "Error restoring student");
      }
    }
  };

  const renderPaymentChip = (rowData) => {
    const s = rowData.paymentSummary;
    if (!s) return <Chip label="No Invoice" size="small" variant="outlined" />;
    if (s.status === 'PAID') return <Chip label="Paid" color="success" size="small" />;
    return <Chip label={`₹${s.totalBalance.toLocaleString('en-IN')} Due`} color="warning" size="small" />;
  };

  const adminHeaders = [
    "Sr no.",
    "Roll No",
    "Name",
    "Phone",
    "Registered On",
    "Organization",
    "Status",
  ];
  const adminColumns = [
    { title: "Sr no.", field: "index", filtering: false },
    { title: "Roll No", field: "displayId", filtering: false },
    { title: "Name", field: "name", filtering: false },
    { title: "Phone", field: "phoneNumber", filtering: false },
    { title: "Registered On", field: "registeredOn", filtering: false },
    { title: "Organization", field: "organization", filtering: false },
    { title: "Status", field: "status", filtering: false },
    { title: "Payment", field: "paymentSummary", filtering: false, render: renderPaymentChip },
    {
      title: "Actions",
      field: "",
      filtering: false,
      render: (rowData) => (
        showDeleted ? (
          <div className="tableActions">
            <Tooltip title="Restore">
              <RestoreFromTrashIcon
                sx={{ color: "success.main", cursor: "pointer" }}
                onClick={() => handleRestoreStudent(rowData)}
              />
            </Tooltip>
          </div>
        ): (  
        <div className="tableActions ">
          <Tooltip title="View">
            <VisibilityIcon
              onClick={() => navigate(`/students/view/${rowData._id}`)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <EditIcon
              onClick={() => navigate(`/students/edit/${rowData._id}`)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <DeleteIcon onClick={() => handleDeleteStudent(rowData)} />
          </Tooltip>
        </div>
        ) 
      ),
    },
  ];

  const studentHeaders = [
    "Sr no.",
    "Roll No",
    "Name",
    "Phone",
    "Registered On",
    "Status",
  ];
  const studentColumns = [
    { title: "Sr no.", field: "index", filtering: false },
    { title: "Roll No", field: "displayId", filtering: false },
    { title: "Name", field: "name", filtering: false },
    { title: "Phone", field: "phoneNumber", filtering: false },
    { title: "Registered On", field: "registeredOn", filtering: false },
    { title: "Status", field: "status", filtering: false },
    { title: "Payment", field: "paymentSummary", filtering: false, render: renderPaymentChip },
    {
      title: "Actions",
      field: "",
      filtering: false,
      render: (rowData) => (
        <div className="tableActions ">
          <Tooltip title="View">
            <VisibilityIcon
              onClick={() => navigate(`/students/view/${rowData._id}`)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <EditIcon
              onClick={() => navigate(`/students/edit/${rowData._id}`)}
            />
          </Tooltip>
          <Tooltip title="Create Invoice">
            <ReceiptLongIcon
              onClick={() => navigate('/invoices/create', { state: { student: rowData } })}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <DeleteIcon onClick={() => handleDeleteStudent(rowData)} />
          </Tooltip>
        </div>
      ),
    },
  ];

  // Fetch data with server-side pagination
  const fetchStudents = async (query) => {
    return new Promise(async (resolve) => {
      try {
        dispatch(setGlobalLoader(true));

        // Build query string for API
        const page = query.page + 1; // MaterialTable uses 0-based index, API uses 1-based
        const limit = query.pageSize;
        const sort = query.orderBy ? { [query.orderBy.field]: query.orderDirection } : {};

        const queryParams = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });

        // Add sort if exists
        if (Object.keys(sort).length > 0) {
          queryParams.append('sort', JSON.stringify(sort));
        }

        // Add date filters
        if (startDate) {
          queryParams.append('startDate', format(startDate, "yyyy-MM-dd"));
        }
        if (endDate) {
          queryParams.append('endDate', format(endDate, "yyyy-MM-dd"));
        }

        // Add search filter
        if (searchTerm) {
          queryParams.append('searchTerm', searchTerm);
        }

        // Add organization filter (admin only)
        if (selectedOrganization) {
          queryParams.append('organizationId', selectedOrganization);
        }

        if (showDeleted) {
          queryParams.append('deleted', 'true');
        }

        if (paymentFilter) {
          queryParams.append('paymentStatus', paymentFilter);
        }

        if (selectedBatch) {
          queryParams.append('batchId', selectedBatch);
        }

        if (statusFilter) {
          queryParams.append('status', statusFilter);
        }

        const studentRes = await getStudents(`?${queryParams.toString()}`);
        const apiData = studentRes?.data?.data;

        const data = apiData?.docs?.map((d, i) => ({
          ...d,
          index: (page - 1) * limit + i + 1,
          registeredOn: format(new Date(d?.createdAt), "MMM dd, yyyy"),
          organization: d?.organizationId?.name,
        }));

        dispatch(setGlobalLoader(false));

        resolve({
          data: data,
          page: query.page,
          totalCount: apiData?.total || 0,
        });
      } catch (error) {
        dispatch(setGlobalLoader(false));
        console.log("get students", error);
        toast.error(error?.response?.data?.msg || "Error fetching students");
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
      }
    });
  };

  return (
    <Box>
      <AppBar
        position={"fixed"}
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
        elevation={0}
      >
        <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
          <Topbar onSidebarOpen={handleSidebarOpen} />
        </Container>
      </AppBar>
      <Sidebar
        onClose={handleSidebarClose}
        open={open}
        variant={isMd ? "permanent" : "temporary"}
      />
      <main>
        <Box height={{ xs: 94, sm: 102, md: 107 }} />
        <Box
          display="flex"
          flex="1 1 auto"
          overflow="hidden"
          paddingLeft={{ md: "256px" }}
        >
          <Box display="flex" flex="1 1 auto" overflow="hidden">
            <Box flex="1 1 auto" height="100%" overflow="auto">
              {profile === PROFILES.ORGANIZATION && (
                <Box mx={{ xs: 3, md: 4 }} mt={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 2,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${alpha('#1565C0', 0.08)} 0%, ${alpha('#1565C0', 0.03)} 100%)`,
                      border: `1px solid ${alpha('#1565C0', 0.25)}`,
                    }}
                  >
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-block',
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                          }}
                        />
                        <Box component="span" sx={{ fontSize: 11, fontWeight: 700, color: 'primary.main', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                          Partnership
                        </Box>
                      </Box>
                      <Box sx={{ fontWeight: 700, fontSize: 15, color: 'text.primary', mb: 0.25 }}>
                        Refer Your Students to Cars24
                      </Box>
                      <Box sx={{ fontSize: 13, color: 'text.secondary' }}>
                        Help your students find their next car. Fill out the form to refer them directly to Cars24.
                      </Box>
                    </Box>
                    <Button
                      variant="contained"
                      size="medium"
                      endIcon={<LaunchIcon sx={{ fontSize: 16 }} />}
                      onClick={() => window.open('https://docs.google.com/forms/d/e/1FAIpQLSdME1f_bOk57pT1NLI2nJaQTzZX3SuzVjtmj4Zql_lQc7PfcA/viewform', '_blank', 'noopener,noreferrer')}
                      sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                    >
                      Refer Now
                    </Button>
                  </Paper>
                </Box>
              )}
              <Stack
                marginTop={3}
                mx={{ xs: 3, md: 4 }}
                gap={2}
              >
                {/* Row 1: Add Student, Download, and Deleted toggle */}
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                  <Box display="flex" gap={2} alignItems="center">
                    {!showDeleted && (
                      <Button
                        variant="contained"
                        color="primary"
                        component="button"
                        size="large"
                        onClick={() => navigate("/students/add")}
                      >
                        Add Student
                      </Button>
                    )}
                    {profile === PROFILES.ADMIN && (
                      <Button
                        variant={showDeleted ? "contained" : "outlined"}
                        color={showDeleted ? "error" : "inherit"}
                        size="large"
                        onClick={() => setShowDeleted((prev) => !prev)}
                      >
                        {showDeleted ? "Show Active" : "Show Deleted"}
                      </Button>
                    )}
                  </Box>
                  {!showDeleted && (
                    <Button
                      variant="outlined"
                      color="primary"
                      component="button"
                      size="medium"
                      onClick={async () => {
                        try {
                          dispatch(setGlobalLoader(true));
                          const exportParams = new URLSearchParams({ limit: "10000" });
                          if (startDate) exportParams.append('startDate', format(startDate, "yyyy-MM-dd"));
                          if (endDate) exportParams.append('endDate', format(endDate, "yyyy-MM-dd"));
                          if (selectedOrganization) exportParams.append('organizationId', selectedOrganization);
                          const studentRes = await getStudents(`?${exportParams.toString()}`);
                          const allStudents = studentRes?.data?.data?.docs?.map((d, i) => ({
                            ...d,
                            index: i + 1,
                            registeredOn: format(new Date(d?.createdAt), "MMM dd, yyyy"),
                            organization: d?.organizationId?.name,
                          }));

                          if (profile === PROFILES.ADMIN) {
                            const rows = allStudents.map((row) => ({
                              "Sr No. ": row.index,
                              rollNo: row.displayId ?? "-",
                              name: row.name,
                              phoneNumber: row.phoneNumber,
                              registeredOn: row.registeredOn,
                              organization: row.organization,
                              status: row.status,
                            }));
                            await exportToExcel(rows, adminHeaders, "Students_admin");
                          } else {
                            const rows = allStudents.map((row) => ({
                              "Sr No. ": row.index,
                              rollNo: row.displayId ?? "-",
                              name: row.name,
                              phoneNumber: row.phoneNumber,
                              registeredOn: row.registeredOn,
                              status: row.status,
                            }));
                            await exportToExcel(rows, studentHeaders, "Students");
                          }
                          dispatch(setGlobalLoader(false));
                        } catch (error) {
                          dispatch(setGlobalLoader(false));
                          console.log("export error", error);
                          toast.error("Error exporting students");
                        }
                      }}
                    >
                      Download
                    </Button>
                  )}
                </Box>

                {/* Row 2: Date Filters (left) and Search (right) */}
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                  {/* Date Filters - Left */}
                  <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                    <Box sx={{ position: "relative" }}>
                      <TextField
                        size="small"
                        value={
                          startDate && endDate
                            ? `${format(startDate, "MMM dd, yyyy")} - ${format(endDate, "MMM dd, yyyy")}`
                            : ""
                        }
                        placeholder="Select date range"
                        onClick={() => setShowDatePicker(!showDatePicker)}
                        InputProps={{
                          readOnly: true,
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarMonthIcon />
                            </InputAdornment>
                          ),
                          endAdornment: startDate && endDate && (
                            <InputAdornment position="end">
                              <ClearIcon
                                sx={{ cursor: "pointer" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDateRange([
                                    {
                                      startDate: null,
                                      endDate: null,
                                      key: "selection",
                                    },
                                  ]);
                                  setShowDatePicker(false);
                                }}
                              />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ minWidth: 280, cursor: "pointer" }}
                      />
                      {showDatePicker && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            zIndex: 1000,
                            mt: 1,
                            boxShadow: 3,
                            borderRadius: 1,
                            bgcolor: "background.paper",
                          }}
                        >
                          <DateRangePicker
                            onChange={handleDateRangeChange}
                            ranges={[
                              {
                                startDate: dateRange[0].startDate || new Date(),
                                endDate: dateRange[0].endDate || new Date(),
                                key: "selection",
                              },
                            ]}
                            minDate={minDateObj}
                            maxDate={today}
                            rangeColors={[theme.palette.primary.main]}
                            locale={enUS}
                          />
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              p: 1,
                              borderTop: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <Button
                              size="small"
                              onClick={() => setShowDatePicker(false)}
                            >
                              Close
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Box>
                    {profile === PROFILES.ADMIN && (
                      <Autocomplete
                        size="small"
                        options={organizations}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') return option;
                          return option?.name || "";
                        }}
                        value={organizations.find(org => org._id === selectedOrganization) || null}
                        onChange={(_, newValue) => setSelectedOrganization(newValue?._id || "")}
                        renderOption={(props, option) => (
                          <li {...props} key={option._id}>
                            {option.name}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Organization"
                            placeholder="Search organization..."
                          />
                        )}
                        sx={{ minWidth: 250 }}
                        isOptionEqualToValue={(option, value) => option?._id === value?._id}
                      />
                    )}
                    <TextField
                      select
                      size="small"
                      label="Payment Status"
                      value={paymentFilter}
                      onChange={(e) => setPaymentFilter(e.target.value)}
                      sx={{ minWidth: 180 }}
                    >
                      <MenuItem value="PAID">Fully Paid</MenuItem>
                      <MenuItem value="PARTIAL">Partially Paid</MenuItem>
                      <MenuItem value="NO_INVOICE">No Invoice</MenuItem>
                    </TextField>

                    <TextField
                      select
                      size="small"
                      label="Batch"
                      value={selectedBatch}
                      onChange={(e) => setSelectedBatch(e.target.value)}
                      sx={{ minWidth: 180 }}
                    >
                      {batches.map((b) => (
                        <MenuItem key={b._id} value={b._id}>{b.name}</MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      select
                      size="small"
                      label="Status"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      sx={{ minWidth: 150 }}
                    >
                      <MenuItem value={STUDENT_STATUS.INPROGRESS}>In Progress</MenuItem>
                      <MenuItem value={STUDENT_STATUS.COMPLETED}>Completed</MenuItem>
                    </TextField>

                    <Button
                      variant="outlined"
                      color="primary"
                      size="medium"
                      onClick={() => {
                        if (tableRef.current) {
                          tableRef.current.onQueryChange();
                        }
                      }}
                    >
                      Filter
                    </Button>
                    <Button
                      variant="text"
                      color="error"
                      size="medium"
                      onClick={() => {
                        setDateRange([
                          {
                            startDate: getDefaultStartDate(),
                            endDate: getDefaultEndDate(),
                            key: "selection",
                          },
                        ]);
                        setSearchTerm("");
                        setSelectedOrganization("");
                        setPaymentFilter("");
                        setSelectedBatch("");
                        setStatusFilter("");
                        setShowDatePicker(false);
                        setTimeout(() => {
                          if (tableRef.current) {
                            tableRef.current.onQueryChange();
                          }
                        }, 100);
                      }}
                    >
                      Reset
                    </Button>
                  </Box>

                  {/* Search - Right */}
                  <Box display="flex" gap={2} alignItems="center">
                    <TextField
                      label="Search by name or phone"
                      type="text"
                      size="small"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && searchTerm.length >= 3 && tableRef.current) {
                          tableRef.current.onQueryChange();
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                        endAdornment: searchTerm && (
                          <InputAdornment position="end">
                            <ClearIcon
                              sx={{ cursor: 'pointer' }}
                              onClick={() => {
                                setSearchTerm("");
                                setTimeout(() => {
                                  if (tableRef.current) {
                                    tableRef.current.onQueryChange();
                                  }
                                }, 100);
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ minWidth: 250 }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      disabled={searchTerm.length < 3}
                      onClick={() => {
                        if (tableRef.current) {
                          tableRef.current.onQueryChange();
                        }
                      }}
                    >
                      Search
                    </Button>
                  </Box>
                </Box>
              </Stack>
              <TableComponent
                ref={tableRef}
                data={fetchStudents}
                columns={
                  profile === PROFILES.ADMIN ? adminColumns : studentColumns
                }
                title={showDeleted ? "Deleted Students" : "Students"}
                isRemoteData={true}
              />
              <Divider />
              {/* <Container paddingY={4}>
                <Footer />
              </Container> */}
            </Box>
          </Box>
        </Box>
      </main>
    </Box>
  );
};

export default Students;
